/**
 * Regras de exibição de unidades de produção e venda.
 *
 * Princípio: quantidade física só pode ser somada dentro da mesma unidade.
 * Kg e saco são a mesma grandeza (60 kg = 1 saco); caixa, maço e bandeja são
 * grandezas independentes e nunca são convertidas entre si.
 *
 * Este arquivo é a única fonte de verdade para rótulos de unidade no frontend.
 */

export const KG_POR_SACO = 60;

export const UNIDADES_PRODUCAO = [
  { value: 'KG', label: 'Quilogramas (kg)' },
  { value: 'SACO', label: 'Sacos (60kg)' },
  { value: 'CAIXA', label: 'Caixas' },
  { value: 'BANDEJA', label: 'Bandejas' },
  { value: 'BIG_BAG', label: 'Big Bags' },
  { value: 'MACO', label: 'Maços' },
  { value: 'UNIDADE', label: 'Unidades' },
];

const SINGULAR = {
  KG: 'kg', SACO: 'saco (60kg)', CAIXA: 'caixa', BANDEJA: 'bandeja',
  BIG_BAG: 'big bag', MACO: 'maço', UNIDADE: 'unidade',
};

const PLURAL = {
  KG: 'kg', SACO: 'sacos (60kg)', CAIXA: 'caixas', BANDEJA: 'bandejas',
  BIG_BAG: 'big bags', MACO: 'maços', UNIDADE: 'unidades',
};

const ABREVIADO = {
  KG: 'kg', SACO: 'sc', CAIXA: 'cx', BANDEJA: 'bdj',
  BIG_BAG: 'bg', MACO: 'mç', UNIDADE: 'un',
};

/** Rótulo no singular: "caixa", "kg", "saco". */
export const labelUnidade = (u) =>
  SINGULAR[u] || u?.toLowerCase().replace(/_/g, ' ') || 'unidade';

/** Rótulo no plural: "caixas", "kg", "sacos". */
export const labelUnidadePlural = (u) =>
  PLURAL[u] || u?.toLowerCase().replace(/_/g, ' ') || 'unidades';

/** Abreviação para tabelas apertadas: "cx", "sc", "kg". */
export const abrevUnidade = (u) =>
  ABREVIADO[u] || u?.toLowerCase().slice(0, 3) || 'un';

/** Kg e saco têm conversão fixa; as outras unidades não têm peso conhecido. */
export const temPeso = (u) => u === 'KG' || u === 'SACO';

const fmtNum = (v, casas = 2) =>
  v != null ? v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }) : '—';

/**
 * Formata uma quantidade com a unidade correta.
 * Ex.: formatarProducao(140, 'KG') → "140,00 kg"
 *      formatarProducao(30, 'CAIXA') → "30,00 caixas"
 */
export function formatarProducao(quantidade, unidade, casas = 2) {
  if (quantidade == null || quantidade === 0) return '—';
  return `${fmtNum(quantidade, casas)} ${labelUnidadePlural(unidade)}`;
}

/** Versão curta, para células de tabela: "140,00 kg" / "30,00 cx". */
export function formatarProducaoCurta(quantidade, unidade, casas = 2) {
  if (quantidade == null || quantidade === 0) return '—';
  return `${fmtNum(quantidade, casas)} ${abrevUnidade(unidade)}`;
}

/** Preço por unidade: "R$ 15,00/caixa". */
export function formatarPrecoUnidade(valor, unidade) {
  if (valor == null || valor === 0) return '—';
  const brl = valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return `${brl}/${labelUnidade(unidade)}`;
}

/**
 * Rótulo de área conforme o tipo: lavoura mede em hectare, estufa em m².
 * Em estufa não faz sentido "custo por m²", então o rótulo é da estufa inteira.
 */
export function labelArea(tipoArea) {
  return tipoArea === 'ESTUFA' ? 'Estufa' : 'Hectare';
}

export function unidadeArea(tipoArea) {
  return tipoArea === 'ESTUFA' ? 'm²' : 'ha';
}

/** "Custo / Hectare" ou "Custo / Estufa". */
export function labelPorArea(prefixo, tipoArea) {
  return `${prefixo} / ${labelArea(tipoArea)}`;
}

/** "Custo / Caixa", "Custo / Saco". */
export function labelPorUnidade(prefixo, unidade) {
  const u = labelUnidade(unidade);
  return `${prefixo} / ${u.charAt(0).toUpperCase()}${u.slice(1)}`;
}

/**
 * Descrição de uma área de cultivo: "Estufa - Grande (500 m²)".
 * Centraliza o padrão usado nos selects de várias telas.
 */
export function descreverArea(lavoura, areaHectares) {
  const tipo = lavoura?.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura';
  const nome = lavoura?.descricao || 'Área';
  const area = areaHectares ? ` (${areaHectares} ${unidadeArea(lavoura?.tipoArea)})` : '';
  return `${tipo} - ${nome}${area}`;
}
